import { Fragment, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";

import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { LoanAccount } from "../../../api/creditAccounts";
import { RelatedAs } from "../../../api/stakeholder_relationships";

import {
  CreditPartyState,
  deriveEmployees,
  deriveSectionIssues,
  derivePrimaryBorrowerSuggestions,
} from "./party-involved/derive";
import LoanAccountsSection from "./party-involved/LoanAccountsSection";
import PartiesSection from "./party-involved/PartiesSection";
import EmployeeSection from "./party-involved/EmployeeSection";
import StepNavigation from "./StepNavigation";
import { isPartyInvolvedSectionsValid } from "../utils/creditValidation";

type CreditAccountPartyInvolvedStepProps = {
  areaOfFraud: OptionNameValuePair<string> | undefined;
  creditParty: CreditPartyState;
  updateCreditParty: (patch: Partial<CreditPartyState>) => void;
  onNext: () => void;
  onPrevious: () => void;
};

const CreditAccountPartyInvolvedStep: React.FC<CreditAccountPartyInvolvedStepProps> = ({
  onNext,
  onPrevious,
  creditParty,
  updateCreditParty,
}) => {

  const employees = useMemo(() => deriveEmployees(creditParty.primaryParties), [creditParty.primaryParties]);
  const suggestions = useMemo(
      () => derivePrimaryBorrowerSuggestions(creditParty.loanAccounts, creditParty.primaryParties),
      [creditParty.loanAccounts, creditParty.primaryParties]
  );

  const partyInvolvedValid = useMemo(
      () => isPartyInvolvedSectionsValid(creditParty.loanAccounts, creditParty.primaryParties, employees),
      [creditParty.loanAccounts, creditParty.primaryParties, employees]
  );

  const sectionIssues = useMemo(
      () => deriveSectionIssues(creditParty.loanAccounts, creditParty.primaryParties, employees),
      [creditParty.loanAccounts, creditParty.primaryParties, employees]
  );

  const flaggedSections = sectionIssues.filter((s) => s.flagged);

  // Flipped on a failed "Next" attempt so every missing field reveals its error.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  // Bump `seq` to ask a section to scroll into view (and open its modal). `seq`
  // increments so the same section can be re-focused on repeated clicks.
  const [focus, setFocus] = useState<{ key: string; seq: number }>({ key: "", seq: 0 });

  const focusCreditSection = (key: string) => {
    setFocus((prev) => ({ key, seq: prev.seq + 1}));
  }

  // Reveal-on-Next: on an invalid attempt, reveal every missing field (and the
  // red "incomplete" markers) and focus the first flagged section instead of
  // silently blocking.
  const handleNext = () => {
    if (!partyInvolvedValid) {
      setShowErrors(true);
      if (flaggedSections.length > 0) focusCreditSection(flaggedSections[0].key);
      return;
    }
    setShowErrors(false);
    onNext();
  };

  const handleEmployeeIdChange = (key: string, employeeId: string) => {
    updateCreditParty({
      primaryParties: creditParty.primaryParties.map((p) =>
        p.id === key
          ? { ...p, employee_details: { ...p.employee_details, employee_id: employeeId } }
          : p
      ),
    });
  };

  const handleLoanAccountsChange = (next: LoanAccount[]) => {

    const accountNameToAccountnumbersMap = new Map<string, string[]>();
    next.forEach((acc) => {
      const name = acc.accountName.trim();
      if (!name || !acc.extAccountNumber.trim()) return;
      if (!accountNameToAccountnumbersMap.has(name)) {
        accountNameToAccountnumbersMap.set(name, []);
      }
      accountNameToAccountnumbersMap.get(name)!.push(acc.accountNumber);
    });

    const validParties = creditParty.primaryParties.filter((party) => {
      const isPrimaryBorrowerParty = party.account_relationships.some(
        (r) => r.related_as === RelatedAs.PRIMARY_BORROWER
      );
      if(isPrimaryBorrowerParty && (accountNameToAccountnumbersMap.get(party.name.trim()) || []).length === 0) {
        return false
      }

      return true;
    })

    const activeAccountNumbers = new Set(next.map((n) => n.accountNumber));

    const updatedParties = validParties.map((party) => {
      const updatedRelationships = party.account_relationships.map(
        (rel) => (rel.related_as === RelatedAs.PRIMARY_BORROWER ?
          {
            ...rel,
            account_numbers: accountNameToAccountnumbersMap.get(party.name.trim()) || [],
          } : {
            ...rel,
            account_numbers: rel.account_numbers.filter((num) => activeAccountNumbers.has(num)),
          }
        )
      )
      return {
        ...party,
        account_relationships: updatedRelationships,
      };
    });

    updateCreditParty({
      loanAccounts: next,
      primaryParties: updatedParties,
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <LoanAccountsSection
          loanAccounts={creditParty.loanAccounts}
          onChange={handleLoanAccountsChange}
          showErrors={showErrors}
          issue={sectionIssues[0]}
          focus={focus}
        />
        <PartiesSection
          parties={creditParty.primaryParties}
          onPartiesChange={(next) => updateCreditParty({ primaryParties: next })}
          loanAccounts={creditParty.loanAccounts}
          suggestions={suggestions}
          showErrors={showErrors}
          issue={sectionIssues[1]}
          focus={focus}
        />
        <EmployeeSection
          employees={employees}
          onEmployeeIdChange={handleEmployeeIdChange}
          showErrors={showErrors}
          issue={sectionIssues[2]}
          focus={focus}
        />
      </div>

      {showErrors && flaggedSections.length > 0 && (
        <div role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle width={16} height={16} className="mt-0.5 shrink-0" />
          <span>
            Complete the required fields in{" "}
            {flaggedSections.map((s, i) => (
              <Fragment key={s.key}>
                <button
                  type="button"
                  className="font-medium underline underline-offset-2 hover:text-red-800"
                  onClick={() => focusCreditSection(s.key)}
                >
                  {s.label}
                  {s.incompleteRows > 0 ? ` (${s.incompleteRows})` : ""}
                </button>
                {i < flaggedSections.length - 1 ? ", " : ""}
              </Fragment>
            ))}
            .
          </span>
        </div>
      )}

      <StepNavigation onNext={handleNext} onPrevious={onPrevious} />
    </>
  );
};

export default CreditAccountPartyInvolvedStep;
